<template>
    <Dialog
        v-model:visible="visible"
        modal
        :closable="false"
        :show-header="false"
        :pt="{ content: { style: { padding: 0 } } }"
        data-testid="fm-dash-praise-modal"
        class="praise-modal-dialog"
        @show="setResponseIdToFirst"
    >
        <div v-if="praiseAction" class="praise-modal">
            <div class="praise-modal__header">
                <FontAwesomeIcon
                    :icon="faThumbsUp"
                    size="xl"
                    class="praise-modal__header__thumb-icon"
                />
                <h4 class="praise-modal__header__title">
                    {{ headerTitle }}
                </h4>
                <button
                    aria-label="Close"
                    autofocus
                    class="praise-modal__header__close"
                    data-testid="fm-dash-close-praise-modal"
                    @click="wipeAndClose()"
                >
                    <FontAwesomeIcon
                        :icon="faClose"
                        size="xl"
                        class="praise-modal__header__close__icon"
                    />
                </button>
            </div>

            <Stepper :value="currentStep">
                <StepItem :value="1">
                    <StepPanel :pt="stepPanelPt">
                        <StepCompose
                            v-model:text-input="praiseTextInput"
                            :responses="praiseAction.responses"
                            :team-members="teamMembers"
                            :input-placeholder="inputPlaceholder"
                            :tags="tags"
                            :show-carousel-indicators="showCarouselIndicators"
                            :is-mobile="isMobile"
                            @response-changed="updateResponseId"
                        />
                    </StepPanel>
                </StepItem>

                <StepItem :value="2">
                    <StepPanel :pt="stepPanelPt">
                        <StepDeliver
                            v-model:selected-send-method="selectedSendMethod"
                            v-model:selected-send-method-data="
                                selectedSendMethodData
                            "
                            v-model:emails="emails"
                            :installed-apps="installedApps"
                            :praisee-email="praiseAction.userEmail"
                            :has-invalid-email="
                                emailsValidation?.hasInvalidEmail
                            "
                            :error-msg="emailsValidation?.errorMsg"
                            :has-mentions-or-tags="hasMentionsOrTags"
                            @send-method-changed="onSendMethodChanged"
                        />
                    </StepPanel>
                </StepItem>
            </Stepper>

            <div class="praise-modal__actions">
                <Button
                    :label="currentStep === 1 ? 'Cancel' : 'Back'"
                    :class="[
                        'praise-modal__actions__button',
                        'praise-modal__actions__button--cancel',
                    ]"
                    :severity="currentStep === 1 ? 'danger' : 'primary'"
                    outlined
                    @click="
                        currentStep === 1 ? wipeAndClose() : (currentStep = 1)
                    "
                />
                <Button
                    class="praise-modal__actions__button"
                    :label="
                        currentStep === 1
                            ? 'Next'
                            : sendingPraise
                              ? 'Sending...'
                              : 'Send Praise'
                    "
                    :disabled="currentStep === 2 && sendingPraise"
                    @click="
                        currentStep === 1 ? goToDeliveryStep() : submitPraise()
                    "
                />
            </div>
        </div>
    </Dialog>
</template>

<script lang="ts" setup>
import { computed, ref, Ref } from 'vue'
import { FontAwesomeIcon } from 'fontawesome/vue-fontawesome'
import { faClose } from 'fontawesome/free-solid-svg-icons'
import { faThumbsUp } from 'fontawesome/pro-regular-svg-icons'
import {
    SEND_METHOD_EMAIL,
    SEND_METHOD_SLACK,
    SEND_METHOD_TEAMS,
    SEND_METHOD_INTERNAL_NOTE,
    MOST_PROMOTER_RESPONSES,
    MOST_IMPROVED_TEAM_MEMBER,
    MOST_POSITIVE_LONG_FORM_RESPONSES,
    RECIPIENT_TYPE_LOCATION,
    ActionResponse,
    PraiseAction,
    SendMethod,
    TeamMember,
} from '@/entities/frontlineManagerDash'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import Stepper from 'primevue/stepper'
import StepItem from 'primevue/stepitem'
import StepPanel from 'primevue/steppanel'
import { sendPraise } from '@/api/frontlineManagerDash'
import { validateEmail } from '@/utils/function'
import { trackAmplitude } from '@/utils/amplitude'
import {
    formatEmailList,
    processMentionsAndTags,
    doesMessageHaveMentionsOrTags,
    getSendMethodLabel,
    getPraiseGeneratorLabel,
} from '@/utils/frontlineManagerDash'
import { capitalizeFirstLetter } from '@/utils/string'
import StepCompose from '@/components/FrontlineManagerDash/PraiseModal/StepCompose.vue'
import StepDeliver from '@/components/FrontlineManagerDash/PraiseModal/StepDeliver.vue'

interface Props {
    praiseAction: PraiseAction | undefined
    installedApps: SendMethod[]
    tags: string[]
    showCarouselIndicators?: boolean
    isMobile?: boolean
}
const props = withDefaults(defineProps<Props>(), {
    showCarouselIndicators: true,
    isMobile: false,
})

const emit = defineEmits([
    'close',
    'completeAction',
    'errorDuringAction',
    'warnDuringAction',
])

const visible = defineModel('visible')
const currentStep = ref(1)
const sendingPraise = ref(false)
const hasMentionsOrTags = ref(false)

const praiseTextInput = ref('')
const responseId = ref(-1)
const teamMembers: Ref<TeamMember[]> = ref([])
const selectedSendMethod = ref(SEND_METHOD_INTERNAL_NOTE)
const selectedSendMethodData = ref({})
const emails = ref('')
const emailsValidation = ref({
    hasInvalidEmail: false,
    errorMsg: '',
})

const stepPanelPt = {
    // Remove default left padding from PrimeVue StepPanel to align content properly
    content: { style: { 'margin-inline-start': '0' } },
    root: { style: { 'padding-inline-start': '0' } },
}

const recipientName = computed(() => {
    if (props.praiseAction?.userName) {
        return capitalizeFirstLetter(props.praiseAction.userName)
    }

    const isRecipientLocation =
        props.praiseAction?.recipientType === RECIPIENT_TYPE_LOCATION &&
        props.praiseAction?.location

    if (isRecipientLocation) {
        return capitalizeFirstLetter(props.praiseAction.location)
    }

    if (props.praiseAction?.recipient?.length) {
        return capitalizeFirstLetter(props.praiseAction.recipient)
    }

    return ''
})

const onSendMethodChanged = () => {
    selectedSendMethodData.value = {}
}

const headerTitle = computed(() => {
    if (currentStep.value === 1) {
        const name = recipientName.value
        return name ? `Praise your team - ${name}` : 'Praise your team'
    } else {
        return 'Where to send Praise?'
    }
})

const inputPlaceholder = computed(() => {
    switch (props.praiseAction?.generator) {
        case MOST_IMPROVED_TEAM_MEMBER:
            return 'Well done! You have the most improved score in the last week! 👏'
        case MOST_PROMOTER_RESPONSES:
            return `Great work! You’ve received ${props.praiseAction.responses.length ?? ''} promoter responses this week! 🎉`
        case MOST_POSITIVE_LONG_FORM_RESPONSES:
            return "Great job! You've received an outstanding comment from a customer! 🌟"
        default:
            return ''
    }
})

const setResponseIdToFirst = () => {
    if (props.praiseAction?.responses?.length) {
        updateResponseId(props.praiseAction.responses[0])
    }
}

const updateResponseId = (response: ActionResponse) => {
    if (response) {
        responseId.value = response.id
        teamMembers.value = response.mentions ?? []
    }
}

const goToDeliveryStep = () => {
    hasMentionsOrTags.value = doesMessageHaveMentionsOrTags(
        praiseTextInput.value,
        teamMembers.value
    )
    currentStep.value = 2
}

const wipeAndClose = () => {
    emit('close')

    // Reset values
    currentStep.value = 1
    praiseTextInput.value = ''
    selectedSendMethod.value = SEND_METHOD_INTERNAL_NOTE
    selectedSendMethodData.value = {}
    emailsValidation.value = {
        hasInvalidEmail: false,
        errorMsg: '',
    }
}

const validateEmailInput = (emailList: string[]) => {
    const hasInvalidEmails =
        !emailList.length ||
        emailList.some((email) => email && !validateEmail(email))

    emailsValidation.value = {
        hasInvalidEmail: hasInvalidEmails,
        errorMsg: hasInvalidEmails
            ? "Required field. All emails must be valid and separated with ',' or ';'"
            : '',
    }

    return emailList.length > 0 && !hasInvalidEmails
}

const submitPraise = async () => {
    let praiseMessage = praiseTextInput.value.trim() || inputPlaceholder.value

    const idToAddInternalNote =
        responseId.value !== -1
            ? responseId.value
            : props.praiseAction?.responses?.[0]?.id ?? -1

    sendingPraise.value = true

    if (selectedSendMethod.value === SEND_METHOD_EMAIL) {
        const emailList = formatEmailList(emails.value)
        const valid = validateEmailInput(emailList)

        if (!valid) {
            sendingPraise.value = false
            emit(
                'warnDuringAction',
                'Please fix the email validation issues before sending.'
            )
            return
        }

        selectedSendMethodData.value = { emails: emailList }
    }

    if (selectedSendMethod.value === SEND_METHOD_INTERNAL_NOTE) {
        praiseMessage = processMentionsAndTags(praiseMessage, teamMembers.value)
    }

    if (
        [SEND_METHOD_SLACK, SEND_METHOD_TEAMS].includes(
            selectedSendMethod.value
        )
    ) {
        if (!selectedSendMethodData.value?.name) {
            sendingPraise.value = false
            emit(
                'warnDuringAction',
                'Please select a channel for the praise to be sent to.'
            )
            return
        }
    }

    await sendPraise(
        props.praiseAction?.slug,
        praiseMessage ?? inputPlaceholder,
        Number(idToAddInternalNote),
        selectedSendMethod.value,
        selectedSendMethodData.value
    )
        .then(() => {
            wipeAndClose()
            const successToast = `Your praise ${recipientName.value ? `to ${recipientName.value}` : ''} has been sent.`
            emit('completeAction', successToast, props.praiseAction?.slug)

            const eventProperties = {
                deliveryMethod: getSendMethodLabel(selectedSendMethod.value),
                praiseType: getPraiseGeneratorLabel(
                    props.praiseAction?.generator
                ),
            }
            trackAmplitude(null, eventProperties, 'Praise Sent')
        })
        .catch(() => {
            emit(
                'errorDuringAction',
                'There was an error while sending your praise. Please try again.'
            )
        })
        .finally(() => {
            sendingPraise.value = false
        })
}
</script>

<style lang="less">
@import '@/styles/prime-vue/variables.less';

.praise-modal-dialog {
    width: calc(100% - (@spacing-lg * 4));
}

@media (min-width: @tabletBreakpoint) {
    .praise-modal-dialog {
        max-width: 480px;
        width: 100%;
    }
}
</style>

<style lang="less" scoped>
@import '@/styles/prime-vue/variables.less';
@import '@/styles/rain/variables.less';

.praise-modal {
    padding: @spacing-2xl @spacing-xl;
    display: flex;
    flex-direction: column;
}

.praise-modal__header {
    display: flex;
    gap: @spacing-md;
    align-items: center;
    margin-bottom: @spacing-lg;

    &__thumb-icon {
        color: var(--p-primary-600);
    }

    &__title {
        margin: 0;
        flex-grow: 2;
        font-weight: @fontWeight-bold;
        color: var(--p-gray-900);
        font-size: 18px;
        line-height: 28px;
    }

    &__close {
        display: flex;
        align-items: center;
        background: unset;
        border: unset;
        color: var(--p-gray-400);
        cursor: pointer;
        transition: color 0.2s ease-in;
        padding: @spacing-md;
        height: 16px;
        width: 16px;
    }
    &__close:hover {
        color: var(--p-error-600);
    }
}

.praise-modal__actions {
    display: flex;
    flex-direction: column;
    gap: @spacing-lg;

    &__button {
        flex: 1;
        font-size: @fontSize-base;
        padding: 13px 16px;

        &--cancel {
            border-color: var(--p-gray-300);
            color: var(--p-gray-700);
        }
    }
}

@media (min-width: @tabletBreakpoint) {
    .praise-modal {
        padding: @spacing-3xl;
    }

    .praise-modal__header {
        margin-bottom: @spacing-2xl;

        &__title {
            font-size: @fontSize-lg;
            line-height: @lineHeight-lg;
        }
    }

    .praise-modal__actions {
        flex-direction: row;
    }
}
</style>
